import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useState } from "react";
import { gql, useReactiveVar } from "@apollo/client";
import styled from 'styled-components';
import { currentBusinessIdVar } from "../helpers/client";
import LoadingScreen from "./LoadingScreen";
import ErrorScreen from "./ErrorScreen";
import NotFoundScreen from "./NotFoundScreen";
import { useUpdateCampaignVideoMutation, useCampaignVideoQuery, useUpdateCampaignPictureMutation, useRemoveCampaignPictureMutation } from "../types";
import TextInput from "./TextInput";
import Button, { ButtonVariant } from "./Button";
import NotShittyUploadcareWidget from "./NotShittyUploadcareWidget";
const VideoIframeWrapper = styled.div `
  height: 0;
  overflow: hidden;
  padding-top: 56.25%;
  background-color: #6f6f6f;
  position: relative;
`;
const VideoIframe = styled.iframe `
  left:0;
  top:0;
  width:100%;
  height:100%;
  position:absolute;
`;
gql `
  query CampaignVideo($id: ID!){
    business(id: $id) {
      video {
        id
      }
      picture {
        id
        cdnPath
        url
      }
    }
  }
  mutation UpdateCampaignPicture($id: ID!, $imageInput: ImageInput!) {
    updateCampaignPicture(businessId: $id, imageInput: $imageInput) {
      picture {
       id
      }
    }
  }
  mutation RemoveCampaignPicture($id: ID!) {
    removeCampaignPicture(businessId: $id) {
      picture {
       id
      }
    }
  }
  mutation UpdateCampaignVideo($id: ID!, $videoInput: VideoInput!) {
    updateCampaignVideo(businessId: $id, videoInput: $videoInput) {
      video {
       id
      }
    }
  }
  
  
`;
const UpdateVideo = () => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const currentBusinessId = useReactiveVar(currentBusinessIdVar);
    const { loading, error, data } = useCampaignVideoQuery({
        variables: { id: currentBusinessId },
    });
    const videoIframeRef = useRef(null);
    const videoIframeOnload = () => {
        videoIframeRef.current.style.height = "100%";
    };
    const videoIframeOnReload = () => {
        if (!videoIframeRef.current.onload)
            videoIframeRef.current.onload = videoIframeOnload;
        videoIframeRef.current.contentWindow.postMessage({ action: 'reload' }, process.env.REACT_APP_WITHFRIENDS_HOST);
        videoIframeRef.current.style.height = "0px";
    };
    const [videoUrl, setVideoUrl] = useState("");
    const [isValidDomain, setIsValidDomain] = useState(true);
    const [isUpdatingVideo, setIsUpdatingVideo] = useState(false);
    const [updateCampaignImage] = useUpdateCampaignPictureMutation({});
    const [removeCampaignPicture] = useRemoveCampaignPictureMutation({
        variables: {
            id: currentBusinessId,
        },
    });
    const [removeCampaignVideo] = useUpdateCampaignVideoMutation({
        variables: {
            id: currentBusinessId,
            videoInput: {}
        },
        onCompleted: videoIframeOnReload,
        refetchQueries: ['CampaignVideo']
    });
    const [updateCampaignVideo] = useUpdateCampaignVideoMutation({
        variables: {
            id: currentBusinessId,
            videoInput: {
                mimeType: 'video/mp4',
                path: videoUrl,
                cdnPath: videoUrl,
            }
        },
        onCompleted: videoIframeOnReload,
        refetchQueries: ['CampaignVideo']
    });
    const resetCampaignVideoIds = (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') ? [100078, 100079] : [1259888, 1259889];
    const [resetCampaignVideo] = useUpdateCampaignVideoMutation({
        variables: {
            id: currentBusinessId,
            videoInput: {
                id: resetCampaignVideoIds[resetCampaignVideoIds.length * Math.random() | 0].toString()
            },
        },
        onCompleted: videoIframeOnReload,
        refetchQueries: ['CampaignVideo']
    });
    const validDomains = [
        'youtube.com',
        'youtu.be',
        'vimeo.com',
    ];
    const validateThenUpdateCampaignVideo = async () => {
        const matches = videoUrl.match(/^https?\:\/\/([^\/?#]+)(?:[\/?#]|$)/i);
        const domain = matches && matches[1];
        if (validDomains.some(validDomain => videoUrl.includes(validDomain))) {
            setIsValidDomain(true);
            setIsUpdatingVideo(true);
            await removeCampaignPicture();
            await updateCampaignVideo();
            setIsUpdatingVideo(false);
        }
        else {
            setIsValidDomain(false);
        }
    };
    const doRemoveCampaignVideo = async () => {
        setIsUpdatingVideo(true);
        await removeCampaignPicture();
        await removeCampaignVideo();
        setIsUpdatingVideo(false);
    };
    const doResetCampaignVideo = async () => {
        setIsUpdatingVideo(true);
        await removeCampaignPicture();
        await resetCampaignVideo();
        setIsUpdatingVideo(false);
    };
    const handleMediaUpload = async (fileInfo) => {
        setTimeout(() => {
            setIsUpdatingVideo(false);
        }, 250);
        if (!fileInfo) {
            return;
        }
        if (fileInfo.isImage) {
            const params = {
                name: fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.name,
                mimeType: fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.mimeType,
                path: fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.cdnUrl,
            };
            await updateCampaignImage({
                variables: {
                    id: currentBusinessId,
                    imageInput: params
                },
            });
            await removeCampaignVideo();
        }
        else {
            await removeCampaignPicture();
            await updateCampaignVideo({
                variables: {
                    id: currentBusinessId,
                    videoInput: {
                        mimeType: fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.mimeType,
                        path: fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.cdnUrl,
                        cdnPath: fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.cdnUrl,
                    }
                }
            });
        }
    };
    const updateVideo = (ev) => {
        setVideoUrl(ev.target.value);
    };
    if (loading) {
        return _jsx(LoadingScreen, {});
    }
    if (error) {
        return _jsx(ErrorScreen, { message: error.message });
    }
    if (!(data === null || data === void 0 ? void 0 : data.business)) {
        return _jsx(NotFoundScreen, {});
    }
    return (_jsxs("div", { children: [_jsx("div", { className: "w-full flex justify-center my-4", children: _jsx("div", { className: "w-full max-w-4xl" + ((((_a = data === null || data === void 0 ? void 0 : data.business) === null || _a === void 0 ? void 0 : _a.video) || ((_b = data === null || data === void 0 ? void 0 : data.business) === null || _b === void 0 ? void 0 : _b.picture)) ? "" : " hidden"), children: _jsxs(VideoIframeWrapper, { children: [_jsx("p", { className: "absolute text-xl text-center text-white top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2", children: "Loading..." }), _jsx(VideoIframe, { ref: videoIframeRef, scrolling: "no", src: `${process.env.REACT_APP_WITHFRIENDS_HOST}/movement/${currentBusinessId}/component_cover` })] }) }) }), _jsx("div", { className: "w-full flex justify-center mt-4", children: _jsxs("div", { className: "w-full", children: [_jsxs("p", { className: "prose max-w-full mb-2", children: ["To ", ((_c = data === null || data === void 0 ? void 0 : data.business) === null || _c === void 0 ? void 0 : _c.video) ? "change" : "set", " your upsell media, paste a YouTube or Vimeo link here for videos, or upload any image or video. We suggest using something that invokes warmth and community, like an interior photo of your space, a picture of your team, or a short video showing the energy of your events. Note that videos will be silent and loop, so aim for 10-30 seconds."] }), _jsx(TextInput, { id: "business-video", value: videoUrl, onChange: updateVideo }), !isValidDomain && (_jsx("div", { className: "font-heavy mb-4 text-salmon-600", children: "Please enter a valid URL from Youtube or Vimeo" })), _jsx("div", { className: "flex flex-row mt-4 max-w-max", children: !isUpdatingVideo ? (_jsxs(_Fragment, { children: [_jsxs(Button, { className: "mr-6", style: { minWidth: "max-content", width: "max-content" }, variant: ButtonVariant.Outlined, onClick: validateThenUpdateCampaignVideo, children: [((_d = data === null || data === void 0 ? void 0 : data.business) === null || _d === void 0 ? void 0 : _d.video) ? "Update Embedded" : "Embed", " Video"] }), _jsx(Button, { className: "mr-6", style: { minWidth: "max-content", width: "max-content" }, variant: ButtonVariant.Outlined, children: _jsx(NotShittyUploadcareWidget, { image: (_e = data === null || data === void 0 ? void 0 : data.business) === null || _e === void 0 ? void 0 : _e.picture, onChangeCallback: handleMediaUpload, onChangeInitiated: () => {
                                                setIsUpdatingVideo(true);
                                            }, label: "Upload Media", imagesOnly: false, inputAcceptTypes: "image/*,video/*", crop: "1600x900" }) }), ((_f = data === null || data === void 0 ? void 0 : data.business) === null || _f === void 0 ? void 0 : _f.video) || ((_g = data === null || data === void 0 ? void 0 : data.business) === null || _g === void 0 ? void 0 : _g.picture) ? (_jsxs(Button, { style: { minWidth: "max-content", width: "max-content" }, variant: ButtonVariant.Quiet, onClick: doRemoveCampaignVideo, children: ["Remove ", ((_h = data === null || data === void 0 ? void 0 : data.business) === null || _h === void 0 ? void 0 : _h.video) ? "Video" : "Image"] })) : (_jsx(Button, { style: { minWidth: "max-content", width: "max-content" }, variant: ButtonVariant.Quiet, onClick: doResetCampaignVideo, children: "Reset to Default Video" }))] })) : (_jsx(Button, { className: "my-4", variant: ButtonVariant.Outlined, disabled: true, children: "Updating..." })) })] }) })] }));
};
export default UpdateVideo;
